<script setup>
import {onMounted, onBeforeUnmount, provide, ref, computed, watch} from "vue";
import {useBoostPaymentStore2} from "@/stores/payment-boost2.js";
import {FloatLabel, MultiSelect} from "primevue";
import FiltersModal from "@/views/payment/FiltersModal.vue";
import Button from "@/components/Button/index.vue";
import {useOrderFilter} from "@/composables/useOrderFilter.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {DataTable, Column} from "primevue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import Checkbox from "primevue/checkbox";
import {useRouter} from "vue-router";
import UpdateHotOrder from "@/views/orders/Details/hotOrders/UpdateHotOrder.vue";
import {showMessage} from "@/utils/showMessage.js";
import {useUserStore} from "@/stores/user.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import HotOrderLog from "@/views/orders/Details/hotOrders/HotOrderLog.vue";
import Summary from "@/views/payment/Summary.vue";
import Popover from "@/views/payment/Popover.vue";
import PaymentModal from "@/views/payment/paymentModal.vue"
import UserProfile from "@/components/UserProfile/index.vue";

const paymentStore = useBoostPaymentStore2();
const userStore = useUserStore();
const router = useRouter();
let interval;
//let clearedInterval = false;
const popover = ref()
const isShowFiltersModal = ref(false);
const isShowHotOrderLog = ref(false);
const isShowPaymentModal = ref(false);
const isShowUserProfileModal = ref(false);
const expandedRowGroups = ref([]);
const selectedCustomers = ref([]);
const ordersForPay = ref([]);
const takerForPay = ref({});
const allSelected = ref(false);
const groupSelection = ref({});
const orders = computed(() => {
  return paymentStore.boostOrders
});
const keyword = ref('')
const isShowUpdateHotOrderModal = ref(false)
const selectedSubOrder = ref({})

const hasInitialized = ref(false);
const isAllExpanded = computed(() => {
  const groups = [...new Set(filtered.value.map((item) => item.takerName))];
  return groups.length > 0 && expandedRowGroups.value.length === groups.length;
});

const sortField = ref('lastUpdate')
const sortOrder = ref(1)

const calculateCustomerTotal = (name) => filtered.value.filter((c) => c.takerName === name).length;

function getTotalValuesByTakerName(takerName) {
  const total = filtered.value
      .filter(item => item.takerName === takerName && item.value)
      .reduce((sum, item) => sum + Number(item.value), 0)

  return Number(total.toFixed(2))
}

function getSelectedCountByTakerName(takerName) {
  return selectedCustomers.value.filter(item => item.takerName === takerName).length;
}

function getSelectedTotalValuesByTakerName(takerName) {
  const total = selectedCustomers.value
      .filter(item => item.takerName === takerName && item.value)
      .reduce((sum, item) => sum + Number(item.value), 0);

  return Number(total.toFixed(2));
}

const {filtered} = useOrderFilter(
    orders,
    keyword,
    'boostSubOrders'
);

const sortedFiltered = computed(() => {
  let data = [...filtered.value]

  if (!sortField.value) return data

  const field = sortField.value
  const order = sortOrder.value ?? 1   // 1 = asc, -1 = desc

  // sort by group only for these columns
  if (['amount', 'creationTime', 'lastUpdate'].includes(field)) {
    return sortByGroupKey(data, field, order)
  }

  return data.sort((a, b) => {
    let valA = a[field]
    let valB = b[field]

    if (field === 'lastUpdate' || field === 'creationTime') {
      valA = Number(valA) || 0
      valB = Number(valB) || 0
    } else if (field === 'amount') {
      valA = Number(valA) || 0
      valB = Number(valB) || 0
    }

    if (valA == null && valB == null) return 0
    if (valA == null) return order
    if (valB == null) return -order

    if (typeof valA === 'number' && typeof valB === 'number') {
      return order * (valA - valB)
    }
    return order * String(valA).localeCompare(String(valB))
  })
})

function sortByGroupKey(data, field, order) {
  const groupKeys = {}
  const groups = [...new Set(data.map(item => item.takerName))]

  groups.forEach(groupName => {
    const groupRows = data.filter(r => r.takerName === groupName)

    if (field === 'amount') {
      groupKeys[groupName] = groupRows.reduce((sum, r) => sum + Number(r.value || 0), 0)
    } else {
      const times = groupRows
          .map(r => Number(r[field] || 0))
          .filter(t => t > 0)

      if (times.length === 0) {
        groupKeys[groupName] = 0
      } else if (order === 1) {
        groupKeys[groupName] = Math.min(...times)   // minimum -> ascending
      } else {
        groupKeys[groupName] = Math.max(...times)   // maximum -> descending
      }
    }
  })

  const sortedGroups = groups.sort((g1, g2) => {
    const k1 = groupKeys[g1] || 0
    const k2 = groupKeys[g2] || 0

    if (field === 'amount') {
      return (k1 - k2) * (-order)
    } else {
      return order * (k1 - k2)
    }
  })

  const groupedRows = {}
  data.forEach(row => {
    if (!groupedRows[row.takerName]) groupedRows[row.takerName] = []
    groupedRows[row.takerName].push(row)
  })

  const result = []
  sortedGroups.forEach(group => {
    result.push(...(groupedRows[group] || []))
  })

  return result
}

const handleSort = (event) => {
  sortField.value = event.sortField
  sortOrder.value = event.sortOrder
}

const showFiltersModal = () => {
  isShowFiltersModal.value = true
}

function showHotOrderLog(data) {
  isShowHotOrderLog.value = true
  selectedSubOrder.value = data
}

const getSubOrders = async () => {
  await paymentStore.action_getSubOrders();
};

const toggleGroup = (groupName) => {
  const checked = groupSelection.value[groupName];
  const groupRows = filtered.value.filter(
      (c) => c.takerName === groupName
  );

  if (checked) {
    selectedCustomers.value = [
      ...new Set([...selectedCustomers.value, ...groupRows]),
    ];
  } else {
    selectedCustomers.value = selectedCustomers.value.filter(
        (c) => c.takerName !== groupName
    );
  }
};

const startLoop = async () => {
  interval = setInterval(async () => {
    if (!paymentStore.isFetching) {
      await getSubOrders();
    }
  }, 2000);
};


/*const fetchNewOrders = async () => {
  if (clearedInterval) return;

  if (orders.value.length > 0 && !paymentStore.isFetching) {
    await getSubOrders();
  }

  if (!clearedInterval) {
    interval = setTimeout(fetchNewOrders, 2000);
  }
};*/

const changeFinalized = (finalized) => {
  paymentStore.resetLastUpdate()
  paymentStore.isImmediateRequest = true
  paymentStore.filters.finalized = finalized
  paymentStore.isLoading = true
}

function showUpdateHotOrderModal({data}) {
  selectedSubOrder.value = data
  isShowUpdateHotOrderModal.value = true
}

function getOrderDetailsRoute(subOrder) {
  const route = {name: 'boost-order-details', params: {id: 0}};

  if (subOrder.uuid) {
    const uuid = subOrder.uuid.split("-")[0];
    route.params.id = uuid;
  } else {
    route.params.id = subOrder.orderID;
  }

  return route;
}

const expandAll = () => {
  expandedRowGroups.value = [...new Set(filtered.value.map((item) => item.takerName))];
};

const collapseAll = () => {
  expandedRowGroups.value = [];
};

const toggleAllGroups = () => {
  if (isAllExpanded.value) {
    collapseAll();
  } else {
    expandAll();
  }
};

const showedOrders = computed(() => selectedCustomers.value.length > 0 ? selectedCustomers.value : filtered.value)

function copyUUIDs() {
  const selectedOrders = selectedCustomers.value.length > 0 ? selectedCustomers.value : filtered.value

  if (!selectedOrders.length) {
    alert("No UUIDs to copy!");
    return;
  }
  const uuids = selectedOrders
      .map(order => order.uuid)
      .filter(Boolean)
      .join('\n');

  navigator.clipboard.writeText(uuids)
      .then(() => {
        showMessage(selectedOrders.length === 1 ? `${selectedOrders.length} UUID copied` : `${selectedOrders.length} UUIDs copied`, 'success');
      })
      .catch(err => {
        console.error("Failed to copy UUIDs:", err);
        showMessage('Failed to copy UUIDs', 'error');
      });
}

const pay = (takerID, takerName) => {
  isShowPaymentModal.value = true
  ordersForPay.value = selectedCustomers.value.filter(item => item.takerID === takerID)
  takerForPay.value = {
    "takerID": takerID,
    "takerName": takerName
  }
}

const showUserProfileModal = (takerID, takerName) => {
  isShowUserProfileModal.value = true
  takerForPay.value = {
    "takerID": takerID,
    "takerName": takerName
  }
}

watch(selectedCustomers, () => {
  const groups = [...new Set(filtered.value.map((c) => c.takerName))];
  groups.forEach((g) => {
    const groupRows = filtered.value.filter(
        (c) => c.takerName === g
    );
    groupSelection.value[g] = groupRows.every((r) =>
        selectedCustomers.value.includes(r)
    );
  });
});

watch(filtered, () => {
  if (!hasInitialized.value && filtered.value.length > 0) {
    selectedCustomers.value = []
    if (!isAllExpanded.value) {
      expandedRowGroups.value = [...new Set(filtered.value.map((c) => c.takerName))];
    } else {
      expandedRowGroups.value = []
    }
    hasInitialized.value = true;
  }
});

onMounted(() => {
  startLoop();
});

onBeforeUnmount(() => {
  if (interval) {
    clearInterval(interval);
  }
});

/*onMounted(() => {
  getSubOrders()
  fetchNewOrders();
});

onBeforeUnmount(() => {
  clearTimeout(interval);
  clearedInterval = true
});*/

provide('isShowFiltersModal', isShowFiltersModal)
provide('isShowUpdateHotOrderModal', isShowUpdateHotOrderModal)
provide('isShowHotOrderLog', isShowHotOrderLog)
provide('isShowPaymentModal', isShowPaymentModal)
provide('isShowUserProfileModal', isShowUserProfileModal)
</script>
<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <div class="flex flex-row justify-between gap-2">
          <div
              class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
            </svg>
          </span>
            <input v-model="keyword"
                   @input="$emit('update:modelValue', $event.target.value)"
                   type="text"
                   class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
            <button v-if="keyword"
                    @click="keyword = ''" class="hover:text-red-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="1.5"
                      d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
              </svg>
            </button>
          </div>
          <button @click="showFiltersModal" class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
                  :class="{'border !border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': paymentStore.isFilterActive}">
            Filters {{ paymentStore.isFilterActive ? '(' + paymentStore.activeFilterCount + ')' : '' }}
          </button>
          <Summary :orders="showedOrders"/>
          <button
              @click="toggleAllGroups"
              class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
          >
            {{ isAllExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
          <div
              class="w-max border border-slate-200 dark:border-slate-700 dark:bg-slate-900 flex items-center rounded-md text-xs h-[32px]">
          <span
              class="flex-1 font-normal px-3 py-1 rounded-r ltr:rounded-l ltr:rounded-r-none transition-all duration-150 cursor-pointer h-full items-center flex"
              :class="
                    paymentStore.filters.finalized === false
                      ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                      : 'dark:text-slate-300'
                  "
              @click="changeFinalized(false)"
          >Delivering</span>
            <span
                class="flex-1 font-normal px-3 py-1 transition-all duration-150 cursor-pointer h-full items-center flex"
                :class="
          paymentStore.filters.finalized === true
            ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
            : 'dark:text-slate-300'
        "
                @click="changeFinalized(true)"
            >Archived</span>
            <span
                class="flex-1 font-normal px-3 py-1 rounded-l ltr:rounded-r ltr:rounded-l-none transition-all duration-150 cursor-pointer h-full items-center flex"
                :class="
                  paymentStore.filters.finalized === null
                    ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                    : 'dark:text-slate-300'
                "
                @click="changeFinalized(null)"
            >All</span>
          </div>
          <button v-if="userStore.hasPermission('BIG_BOSS')" @click="copyUUIDs"
                  class="btn btn-outline-dark p-1 text-white h-[32px]">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
              <rect width="336" height="336" x="128" y="128" fill="none" stroke="currentColor" stroke-linejoin="round"
                    stroke-width="32" rx="57" ry="57"/>
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"
                    d="m383.5 128l.5-24a56.16 56.16 0 0 0-56-56H112a64.19 64.19 0 0 0-64 64v216a56.16 56.16 0 0 0 56 56h24"/>
            </svg>
          </button>
        </div>
      </div>
      <div v-if="paymentStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :lazy="true"
                 :value="sortedFiltered"
                 :sortField="sortField"
                 :sortOrder="sortOrder"
                 @sort="handleSort"
                 scrollable
                 scrollHeight="flex"
                 resizableColumns
                 removableSort
                 class="text-xs w-full flex-1 relative"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed; flex: 1 1 0%; position: absolute"
                 dataKey="id"
                 @row-click="showUpdateHotOrderModal"
                 v-model:selection="selectedCustomers"
                 v-model:expandedRowGroups="expandedRowGroups"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#1A2941] !border-y !text-slate-300'"
                 expandableRowGroups
                 rowGroupMode="subheader"
                 groupRowsBy="takerName"
                 :pt="{
                    column:{headerCell: { class: '!text-slate-200'}},
                    rowGroupHeader: { class: '!font-medium text-sm relative z-[1] !bg-[#121E31] !text-slate-200' },
                    rowGroupHeaderCell: { class: '!px-1 !py-0', colspan: 13 },
                    rowGroupFooterCell:{ colspan : 13}
                }"
      >
        <!-- Group Header -->
        <template #groupheader="slotProps">
          <Checkbox
              v-model="groupSelection[slotProps.data.takerName]"
              :binary="true"
              @change="toggleGroup(slotProps.data.takerName)"
          />
          <span
              :class="['flex-1 align-middle items-center leading-normal inline-flex flex-row !text-sm w-[calc(100%-52px)]', expandedRowGroups.includes(slotProps.data.takerName) ? 'text-yellow-500' : '']">
            <span @click="showUserProfileModal(slotProps.data.takerID, slotProps.data.takerName)"
                  class="ml-2 size-5 rounded-full flex items-center border border-slate-500 text-slate-500 justify-center cursor-pointer hover:border-yellow-500 hover:text-yellow-500">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none"/>
                <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
                  <path
                      d="M19.727 20.447c-.455-1.276-1.46-2.403-2.857-3.207S13.761 16 12 16s-3.473.436-4.87 1.24s-2.402 1.931-2.857 3.207"/>
                  <circle cx="12" cy="8" r="4"/>
                </g>
              </svg>
            </span>
            <span @mouseover="popover.displayPopover($event, slotProps.data.takerName)"
                  @mouseleave="popover.hidePopover()"
                  class="ml-2 min-w-[4%] w-[8%] truncate">
              {{ slotProps.data.takerName }}
            </span>
            <span class="float-right text-xs leading-loose ml-4">
                {{
                calculateCustomerTotal(slotProps.data.takerName) > 1 ? calculateCustomerTotal(slotProps.data.takerName) + ' Orders' : calculateCustomerTotal(slotProps.data.takerName) + ' Order'
              }}&nbsp;-&nbsp;
              {{ getTotalValuesByTakerName(slotProps.data.takerName) + ' $' }}

              <span
                  v-if="getSelectedCountByTakerName(slotProps.data.takerName) > 0"
                  class="bg-blue-900 px-2 rounded-md ml-4">
                  Selected:&nbsp;
                  {{
                  getSelectedCountByTakerName(slotProps.data.takerName) > 1
                      ? getSelectedCountByTakerName(slotProps.data.takerName) + ' Orders'
                      : getSelectedCountByTakerName(slotProps.data.takerName) + ' Order'
                }}&nbsp;-&nbsp;
                  {{ getSelectedTotalValuesByTakerName(slotProps.data.takerName) + ' $' }}
              </span>
            </span>

            <span v-if="getSelectedCountByTakerName(slotProps.data.takerName) > 0"
                  class="flex items-center gap-2 border border-slate-400 text-slate-400 cursor-pointer px-2 rounded-md ml-4 hover:bg-slate-400 hover:text-slate-950"
                  @click="pay(slotProps.data.takerID, slotProps.data.takerName)">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path fill="currentColor"
                      d="M23.8 3a1.5 1.5 0 0 0-1.5-1.5h-14A1.5 1.5 0 0 0 6.8 3v8a1.5 1.5 0 0 0 1.5 1.5h14a1.5 1.5 0 0 0 1.5-1.5Zm-2 7.25a.25.25 0 0 1-.25.25H9.05a.25.25 0 0 1-.25-.25V3.78a.25.25 0 0 1 .25-.25h12.5a.25.25 0 0 1 .25.25Z"/>
                <path fill="currentColor"
                      d="M13.3 7.03a2 2 0 1 0 4 0a2 2 0 1 0-4 0m6.9 9.5l-3.53 1.17a.23.23 0 0 0-.15.14A2 2 0 0 1 14.7 19h-4a.5.5 0 0 1-.5-.5a.5.5 0 0 1 .5-.5h4a1 1 0 0 0 0-2h-3.5a7.15 7.15 0 0 0-4-1.5H5.14a4 4 0 0 0-1.79.5l-3 1.51a.25.25 0 0 0-.13.22v5.4a.26.26 0 0 0 .13.22a.25.25 0 0 0 .25 0l3.22-2a1 1 0 0 1 .85-.1c10 3.36 6.63 3.37 17.87-2.31a.52.52 0 0 0 .06-.94a2.48 2.48 0 0 0-2.4-.47"/>
              </svg>
              Pay
            </span>
          </span>
        </template>

        <!-- Checkbox Column -->
        <Column selectionMode="multiple" style="width: 3%; min-width: 36px; height: 30px"
                headerStyle="min-width: 36px !important"></Column>

        <Column field="uuid" header="UUID" :sortable="true" style="width: 10%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop>{{ slotProps.data.uuid }}</span>
          </template>
        </Column>
        <Column field="game" header="Game" :sortable="true" style="width: 5%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="title" header="Title" :sortable="true" style="width: 10%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>

        <Column field="notes" header="Notes" :sortable="true" style="width: 10%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop
                  @mouseover="popover.displayPopover($event, slotProps.data.notes)"
                  @mouseleave="popover.hidePopover()">
             {{ slotProps.data.notes }}
            </span>
          </template>
        </Column>

        <Column field="description" header="Description" :sortable="true" style="width: 10%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop
                  @mouseover="popover.displayPopover($event, slotProps.data.description)"
                  @mouseleave="popover.hidePopover()">
             {{ slotProps.data.description }}
            </span>
          </template>
        </Column>

        <Column field="amount" header="Amount" :sortable="true" style="width: 5%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>

        <Column field="currency" header="Currency" :sortable="true" style="width: 5%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>

        <Column field="state" header="State" :sortable="true" style="width: 6%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <StateColumn :state="slotProps.data.state"/>
          </template>
        </Column>

        <Column field="parentState" header="Parent State" :sortable="true" style="width: 6%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <StateColumn :state="slotProps.data.parentState"/>
          </template>
        </Column>

        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer"
                style="width: 7%; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
            </span>
          </template>
        </Column>

        <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer"
                style="width: 7%; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
            </span>
          </template>
        </Column>

        <Column class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer"
                style="width: 4%; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <div class="flex flex-row gap-2">
              <RouterLink
                  :to="getOrderDetailsRoute(slotProps.data)"
                  target="_blank"
                  class="group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-info-icon lucide-info group-hover:stroke-blue-400">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
              </RouterLink>
              <button @click.stop="showHotOrderLog(slotProps.data)" class="group">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-history-icon lucide-history group-hover:stroke-blue-400">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                  <path d="M3 3v5h5"/>
                  <path d="M12 7v5l4 2"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
      <Popover ref="popover"/>
    </div>
    <UpdateHotOrder v-if="isShowUpdateHotOrderModal" orderType="Boost" modalType="subOrder"
                    :selectedHotOrder="selectedSubOrder"/>
    <FiltersModal v-if="isShowFiltersModal"/>
    <HotOrderLog v-if="isShowHotOrderLog" :hotOrder="selectedSubOrder"/>
    <PaymentModal v-if="isShowPaymentModal" :orders="ordersForPay" :taker="takerForPay"/>
    <UserProfile v-if="isShowUserProfileModal" :userID="takerForPay.takerID" managePayment/>
  </div>
</template>
<style>
.p-datatable-row-toggle-button {
  vertical-align: middle;
}

.p-checkbox {
  vertical-align: middle !important;
}

.p-datatable-row-toggle-button svg.p-icon {
  width: 12px;
  height: 12px;
}
</style>